/**
 * Voice module design tokens.
 *
 * The module previously used five different ad-hoc dark surfaces (#111111,
 * #1a1a1a, #222222, #2a2a2a, plus Tailwind's gray-800) with no rule about
 * which meant what — table headers were #222222 on two screens and #111111/50
 * on a third, row hovers disagreed the same way. These name the surfaces so
 * the disagreement can't recur.
 */

// Surfaces
export const SURFACE_PAGE = 'bg-gray-50 dark:bg-[#0d0d0d]';
export const SURFACE_CARD = 'bg-white dark:bg-[#161616]';

/**
 * Translucent surface for sticky headers sitting over scrolling content.
 *
 * Must be its own token, NOT `${SURFACE_CARD}/95`: that interpolation yields
 * `bg-white dark:bg-[#161616]/95`, so the opacity attaches only to the dark
 * class and the untouched `bg-white` keeps applying in dark mode — which
 * rendered the Voice page headers as a white bar with white text on them.
 */
export const SURFACE_HEADER = 'bg-white/95 dark:bg-[#161616]/95';
export const SURFACE_SUNKEN = 'bg-gray-50 dark:bg-[#111111]';
export const SURFACE_INPUT = 'bg-white dark:bg-[#1e1e1e]';
export const SURFACE_HOVER = 'hover:bg-gray-50 dark:hover:bg-white/[0.03]';

// Borders
export const BORDER = 'border-gray-200 dark:border-white/[0.08]';
export const BORDER_SUBTLE = 'border-gray-100 dark:border-white/[0.05]';

// Text
export const TEXT_PRIMARY = 'text-gray-900 dark:text-white';
export const TEXT_SECONDARY = 'text-gray-600 dark:text-gray-300';
export const TEXT_MUTED = 'text-gray-500 dark:text-gray-400';

// Shape — one radius for cards, one for controls. The module previously mixed
// rounded-lg and rounded-xl for the same kind of element.
export const RADIUS_CARD = 'rounded-xl';
export const RADIUS_CONTROL = 'rounded-lg';

// The card shell, used by every panel in the module.
export const CARD = `${SURFACE_CARD} ${RADIUS_CARD} border ${BORDER}`;

/**
 * Semantic status tones.
 *
 * Six separate badge factories each defined their own colour map, and they
 * disagreed: yellow meant "pending" on one screen, "draft" on another and
 * "no answer" on a third. One map, one meaning per tone.
 */
export const TONES = {
  success: {
    pill: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-400/20',
    icon: 'text-emerald-500',
    chip: 'bg-emerald-500/10 text-emerald-500',
  },
  danger: {
    pill: 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-400/20',
    icon: 'text-red-500',
    chip: 'bg-red-500/10 text-red-500',
  },
  warning: {
    pill: 'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-400/20',
    icon: 'text-amber-500',
    chip: 'bg-amber-500/10 text-amber-500',
  },
  info: {
    pill: 'bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-500/10 dark:text-blue-400 dark:ring-blue-400/20',
    icon: 'text-blue-500',
    chip: 'bg-blue-500/10 text-blue-500',
  },
  accent: {
    pill: 'bg-orange-50 text-orange-700 ring-orange-600/20 dark:bg-orange-500/10 dark:text-orange-400 dark:ring-orange-400/20',
    icon: 'text-orange-500',
    chip: 'bg-orange-500/10 text-orange-500',
  },
  neutral: {
    pill: 'bg-gray-100 text-gray-600 ring-gray-500/20 dark:bg-white/[0.06] dark:text-gray-400 dark:ring-white/10',
    icon: 'text-gray-400',
    chip: 'bg-gray-500/10 text-gray-400',
  },
};

/**
 * Every status string the Voice module renders, mapped to a tone.
 *
 * Screens used to each own a partial copy of this. Keys are lowercased on
 * lookup so ANSWERED / answered / Answered all resolve.
 */
export const STATUS_TONES = {
  // Lifecycle
  active: 'success',
  answered: 'success',
  completed: 'success',
  connected: 'success',
  healthy: 'success',
  provisioned: 'success',
  registered: 'success',

  pending: 'warning',
  provisioning: 'warning',
  porting_in: 'warning',
  draft: 'warning',
  ringing: 'warning',
  early: 'warning',
  holding: 'warning',
  no_answer: 'warning',
  busy: 'warning',

  failed: 'danger',
  suspended: 'danger',
  unhealthy: 'danger',
  rejected: 'danger',
  cancel: 'danger',
  canceled: 'danger',

  inactive: 'neutral',
  released: 'neutral',
  unassigned: 'neutral',
  unknown: 'neutral',
};

/** Resolve a raw status string to a tone key. */
export const toneFor = (status) => {
  if (!status) return 'neutral';
  const key = String(status).toLowerCase().replace(/[\s-]+/g, '_');
  return STATUS_TONES[key] || 'neutral';
};

/** Human label for a status string: NO_ANSWER -> "No answer". */
export const labelFor = (status) => {
  if (!status) return 'Unknown';
  const s = String(status).replace(/[_-]+/g, ' ').trim().toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// Buttons — one scale, not the four the module had.
export const BTN_BASE =
  'inline-flex items-center justify-center gap-2 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
export const BTN_SIZES = {
  sm: 'h-8 px-3 text-[13px] rounded-lg',
  md: 'h-9 px-4 text-sm rounded-lg',
  lg: 'h-10 px-5 text-sm rounded-lg',
};
export const BTN_VARIANTS = {
  primary: 'bg-orange-600 text-white hover:bg-orange-700 active:bg-orange-800',
  secondary:
    'border border-gray-200 dark:border-white/[0.08] text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/[0.04]',
  ghost: 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06]',
  danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
  success: 'bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800',
};

/** Compose a button className. */
export const btn = (variant = 'primary', size = 'md', extra = '') =>
  `${BTN_BASE} ${BTN_SIZES[size] || BTN_SIZES.md} ${BTN_VARIANTS[variant] || BTN_VARIANTS.primary} ${extra}`.trim();
